/**
 * Artigos do blog: vendas, WhatsApp e escolha de sistema
 */

import type { BlogArticle } from '../types';

const PUBLISHED = '2026-09-27';

export const VENDAS_ARTICLES: BlogArticle[] = [
  {
    slug: 'robo-de-whatsapp-para-delivery',
    title: 'Robô de WhatsApp para delivery: como vender no automático sem perder pedidos',
    description:
      'Como funciona um robô de WhatsApp para delivery, o que ele resolve no horário de pico, o que observar antes de contratar e como configurar em um dia.',
    excerpt:
      'O WhatsApp lotado na sexta à noite custa vendas. Veja como um robô atende, monta o pedido e libera sua equipe para produzir.',
    category: 'Vendas',
    keywords: [
      'robô de WhatsApp para delivery',
      'robô de vendas WhatsApp',
      'chatbot para restaurante',
      'atendimento automático delivery',
      'pedido pelo WhatsApp automático',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'Quem trabalha com delivery conhece a cena: sexta-feira, 20h, o celular não para. São dezenas de conversas abertas, áudios para ouvir, cliente perguntando o preço da pizza grande e alguém anotando o pedido na pressa. Nessa hora, **cada minuto sem resposta é um cliente que pede no concorrente**.',
      },
      {
        type: 'p',
        text: 'Um robô de WhatsApp para delivery existe para resolver exatamente isso: ele atende todo mundo ao mesmo tempo, mostra o cardápio, monta o pedido e só chama uma pessoa quando é realmente necessário.',
      },
      { type: 'h2', text: 'O que um robô de WhatsApp faz na prática' },
      {
        type: 'ul',
        items: [
          '**Responde na hora**, inclusive quando a equipe está ocupada na cozinha ou no balcão.',
          '**Mostra o cardápio atualizado**, com preços e itens disponíveis, sem foto de cardápio desatualizada.',
          '**Monta o pedido completo**: itens, quantidades, observações, cupom, entrega ou retirada, endereço e forma de pagamento.',
          '**Envia o pedido pronto para o painel e para a cozinha**, sem ninguém redigitar nada.',
          '**Avisa o cliente sobre cada etapa**: pedido confirmado, em preparo, saiu para entrega, pronto para retirar.',
          '**Passa para um atendente** quando o cliente pede ou quando o assunto foge do cardápio.',
        ],
      },
      { type: 'h2', text: 'Robô de opções ou robô com inteligência artificial?' },
      {
        type: 'p',
        text: 'Existem dois modelos no mercado. O **robô de opções** conduz a conversa por menus numerados ("1 - Ver cardápio, 2 - Meus pedidos"). É previsível, rápido e não inventa informação. O **robô com IA generativa** entende texto livre e áudio, mas precisa de mais cuidado para não responder algo errado sobre preço ou prazo.',
      },
      {
        type: 'p',
        text: 'Para a maioria dos restaurantes, o que importa é o resultado: o pedido chegar certo e rápido. Um fluxo por opções, bem desenhado, resolve a grande maioria dos pedidos e não depende de o cliente escrever do jeito "certo".',
      },
      { type: 'h2', text: 'O que observar antes de contratar' },
      {
        type: 'ol',
        items: [
          '**O pedido entra direto no sistema?** Se alguém precisa copiar o pedido do WhatsApp para outro lugar, o problema continua.',
          '**Usa o seu número de WhatsApp?** O cliente deve falar com o número que já conhece.',
          '**O cardápio é o mesmo da loja online?** Dois cardápios diferentes geram preço errado.',
          '**Tem aviso de status automático?** Isso reduz muito o "cadê meu pedido?".',
          '**Cobra por mensagem ou por pedido?** Prefira mensalidade fixa: o custo não cresce quando você vende mais.',
          '**Tem teste grátis?** Teste no seu horário de pico antes de decidir.',
        ],
      },
      {
        type: 'tip',
        title: 'Dica',
        text: 'Mesmo com robô, deixe o link da sua loja online na mensagem de boas-vindas. Muitos clientes preferem montar o pedido no cardápio visual, com fotos, e o robô continua cuidando dos avisos.',
      },
      { type: 'h2', text: 'Quanto custa atender o WhatsApp na mão' },
      {
        type: 'p',
        text: 'Faça a conta com os seus números. Um exemplo: se um atendente leva, em média, **4 minutos** para tirar um pedido pelo WhatsApp (mandar cardápio, tirar dúvida, confirmar endereço, passar o total) e a loja recebe **40 pedidos numa noite**, são **160 minutos**, quase 3 horas de uma pessoa só digitando. No pico, isso vira fila, e fila vira cliente desistindo.',
      },
      {
        type: 'p',
        text: 'O robô não elimina o atendimento humano; ele tira da frente o trabalho repetitivo. A equipe passa a responder só o que exige uma pessoa: um pedido especial, uma reclamação, uma dúvida fora do cardápio.',
      },
      { type: 'h2', text: 'Como é uma conversa com o robô, passo a passo' },
      {
        type: 'ol',
        items: [
          'O cliente manda "oi" e recebe um **menu numerado**: ver cardápio, meus pedidos, cartão fidelidade, meus dados, endereço da loja, horário de atendimento e falar com atendente.',
          'Ao escolher o cardápio, vê as **categorias** e depois os produtos, com preços.',
          'Escolhe o item e a quantidade. O robô mostra o **carrinho** e pergunta se quer continuar comprando, ver o carrinho ou fechar o pedido.',
          'No carrinho, dá para **editar itens, aplicar cupom** ou limpar tudo.',
          'Ao fechar, escolhe **entrega ou retirada**, confirma o endereço e, se a loja permitir, **agenda** para outro horário.',
          'Escolhe a forma de pagamento. No **PIX**, recebe o código copia-e-cola e o **QR Code** na própria conversa.',
          'Confirma o resumo e o pedido entra no painel. A partir daí, recebe os **avisos de status** automaticamente.',
        ],
      },
      {
        type: 'p',
        text: 'Se a loja estiver fechada, o robô avisa e mostra o horário de atendimento; com agendamento ligado, oferece os horários disponíveis. Em "Meus pedidos", o cliente pode **pedir de novo** o mesmo pedido com um toque ou acompanhar o que está em andamento.',
      },
      { type: 'h2', text: 'Como colocar para rodar em um dia' },
      {
        type: 'ol',
        items: [
          '**Revise o cardápio**: nomes claros, preços certos, fotos e categorias na ordem em que o cliente procura.',
          '**Configure horários de funcionamento** e, se fizer sentido, o agendamento de pedidos.',
          '**Defina entrega e retirada**: taxa por bairro, pedido mínimo e tempo estimado.',
          '**Ative as formas de pagamento**. Com PIX automático, o pagamento é confirmado sem o cliente mandar comprovante.',
          '**Conecte o WhatsApp** lendo o QR Code com o celular do número da loja.',
          '**Faça um pedido de teste** do seu próprio celular, do "oi" até a confirmação.',
          '**Avise os clientes**: status do WhatsApp, Instagram e um cartaz no balcão com o número e o link da loja.',
        ],
      },
      { type: 'h2', text: 'Erros comuns que fazem o robô vender menos' },
      {
        type: 'ul',
        items: [
          '**Cardápio desatualizado**: item esgotado aparecendo gera pedido cancelado e cliente irritado. Pause o produto no sistema quando acabar.',
          '**Esconder o atendente**: deixe sempre a opção de falar com uma pessoa. Cliente que não encontra ajuda desiste.',
          '**Mandar mensagem em massa sem pedido do cliente**: além de incomodar, aumenta o risco de o número ser bloqueado pelo WhatsApp. Use campanhas com moderação e para quem já é cliente.',
          '**Não acompanhar os números**: sem medir, não dá para saber se o robô está ajudando.',
        ],
      },
      { type: 'h2', text: 'O que acompanhar depois de ligar o robô' },
      {
        type: 'table',
        head: ['Indicador', 'Por que olhar'],
        rows: [
          ['Pedidos pelo WhatsApp por dia', 'Mostra se o canal está crescendo'],
          ['Tempo até o pedido entrar', 'Deve cair bastante em relação ao atendimento manual'],
          [
            'Carrinhos abandonados',
            'Clientes que começaram e não terminaram; dá para recuperar com lembrete',
          ],
          ['Pedidos repetidos', 'O "pedir de novo" é um bom sinal de cliente fiel'],
          ['Conversas passadas para atendente', 'Se for muito alto, falta informação no cardápio'],
        ],
      },
      { type: 'h2', text: 'Como funciona no Vende Aí' },
      {
        type: 'p',
        text: 'No Vende Aí, você conecta o WhatsApp do seu negócio lendo um QR Code, como no WhatsApp Web. O robô mostra o cardápio da loja, monta o carrinho, aplica cupom, pergunta se é entrega ou retirada, agenda quando for o caso e pede a forma de pagamento. O pedido cai no painel como qualquer outro, e o cliente recebe os avisos de status. Tudo com **mensalidade fixa e sem comissão por pedido**.',
      },
      {
        type: 'ul',
        items: [
          '**PIX com QR Code na conversa** e confirmação automática quando o PIX é recebido pelo seu banco ou gateway.',
          '**Cartão fidelidade** e histórico de pedidos acessíveis pelo menu do robô.',
          '**Recuperação de carrinho abandonado**: o cliente que parou no meio recebe um lembrete, no tempo que você configurar.',
          '**Loja fechada?** O robô informa o horário e, com agendamento ligado, aceita pedidos para depois.',
          '**Opção de falar com atendente** sempre disponível no menu.',
        ],
      },
      {
        type: 'p',
        text: 'Veja também como ele funciona para [pizzarias](/para/pizzaria) e [hamburguerias](/para/hamburgueria).',
      },
      {
        type: 'cta',
        text: 'Teste o robô de WhatsApp do Vende Aí por 30 dias, sem cartão de crédito.',
      },
    ],
    faq: [
      {
        question: 'Robô de WhatsApp para delivery funciona com o meu número atual?',
        answer:
          'Na maioria das soluções, sim. No Vende Aí você conecta o número do seu negócio lendo um QR Code, e o cliente continua falando com o mesmo número.',
      },
      {
        question: 'O robô substitui o atendente?',
        answer:
          'Ele assume o trabalho repetitivo (mostrar cardápio, montar pedido, avisar status) e libera a equipe para produzir. Quando o cliente precisa de algo fora do cardápio, a conversa vai para uma pessoa.',
      },
      {
        question: 'O robô usa a API oficial do WhatsApp?',
        answer:
          'No Vende Aí, a conexão é feita com o número do próprio negócio, lendo um QR Code, como no WhatsApp Web. Não há custo por mensagem. Para evitar bloqueios, use o robô para atender quem chama e evite disparos em massa para quem não é cliente.',
      },
      {
        question: 'O robô aceita pedidos com a loja fechada?',
        answer:
          'Ele avisa que a loja está fechada e mostra o horário de atendimento. Se o agendamento estiver ativado, o cliente pode marcar o pedido para um horário disponível.',
      },
      {
        question: 'Quanto custa um robô de WhatsApp para delivery?',
        answer:
          'Depende do fornecedor: há cobrança por mensagem, por pedido ou mensalidade fixa. No Vende Aí o robô faz parte do plano, com mensalidade fixa e pedidos ilimitados.',
      },
    ],
  },
  {
    slug: 'sistema-de-delivery-proprio',
    title: 'Sistema de delivery próprio: o que é, quanto custa e como escolher o seu',
    description:
      'Entenda o que é um sistema de delivery próprio, quanto custa, quais recursos são essenciais e como escolher sem cair em taxa por pedido.',
    excerpt:
      'Ter um canal de vendas próprio é a forma mais direta de parar de depender de comissão. Veja o que um bom sistema precisa ter.',
    category: 'Delivery',
    keywords: [
      'sistema de delivery',
      'sistema de delivery próprio',
      'sistema para delivery de comida',
      'plataforma de delivery própria',
      'aplicativo de delivery próprio',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    relatedSegment: 'restaurante',
    blocks: [
      {
        type: 'p',
        text: 'Um **sistema de delivery próprio** é o conjunto de ferramentas que permite ao seu restaurante vender direto para o cliente, sem intermediário: uma loja online com o seu nome, um painel para receber os pedidos e as ferramentas para cobrar, entregar e trazer o cliente de volta.',
      },
      {
        type: 'p',
        text: 'A diferença para um marketplace é simples: no marketplace o cliente é do aplicativo e você paga comissão sobre cada venda. No delivery próprio, **o cliente e os dados são seus** e o custo é fixo.',
      },
      { type: 'h2', text: 'Recursos essenciais' },
      {
        type: 'table',
        head: ['Recurso', 'Por que importa'],
        rows: [
          [
            'Cardápio digital com link próprio',
            'É a sua loja: você divulga no Instagram, no WhatsApp e no Google',
          ],
          ['Painel de pedidos em tempo real', 'Nada se perde entre o pedido e a cozinha'],
          ['Pagamento online (PIX e cartão)', 'Menos troco, menos calote e confirmação automática'],
          ['Taxa de entrega por região', 'Cobra o valor certo de cada bairro'],
          ['Robô ou automação de WhatsApp', 'Atende no pico sem contratar mais gente'],
          ['Cupons e fidelidade', 'Faz o cliente voltar sem depender de anúncio'],
          ['Relatórios', 'Mostra o que vende, quando e para quem'],
        ],
      },
      { type: 'h2', text: 'Como funciona no dia a dia' },
      {
        type: 'ol',
        items: [
          'O cliente abre o **link da sua loja** (no Instagram, no WhatsApp ou no Google) e escolhe os produtos no cardápio digital.',
          'Informa entrega ou retirada, endereço e forma de pagamento. Se pagar com **PIX ou cartão online**, o valor vai direto para a sua conta.',
          'O pedido aparece no **painel em tempo real**, com alerta sonoro, e vai para a cozinha.',
          'Você muda o status (aceito, em preparo, saiu para entrega) e o cliente é **avisado automaticamente**.',
          'O cliente fica na sua base: da próxima vez, você pode oferecer cupom, pontos ou cashback para ele pedir de novo.',
        ],
      },
      { type: 'h2', text: 'Quanto custa um sistema de delivery próprio' },
      {
        type: 'p',
        text: 'Os modelos mais comuns são três: **mensalidade fixa**, **mensalidade por faixa de pedidos** (o preço sobe conforme você vende mais) e **taxa por pedido**. Para quem já vende bem, a mensalidade fixa costuma ser a mais barata, porque o custo não cresce junto com o faturamento.',
      },
      {
        type: 'p',
        text: 'Some também as **taxas de pagamento online** (cobradas pelo banco ou gateway, não pelo sistema) e verifique se há **taxa de implantação** ou **fidelidade contratual**.',
      },
      {
        type: 'table',
        head: [
          'Pedidos/mês',
          'Ticket médio',
          'Taxa de R$ 1 por pedido',
          'Comissão de 12%',
          'Mensalidade fixa',
        ],
        rows: [
          ['300', 'R$ 50', 'R$ 300', 'R$ 1.800', 'Não muda'],
          ['800', 'R$ 60', 'R$ 800', 'R$ 5.760', 'Não muda'],
          ['1.500', 'R$ 60', 'R$ 1.500', 'R$ 10.800', 'Não muda'],
        ],
      },
      {
        type: 'p',
        text: 'Os valores da tabela são exemplos para comparar os modelos, não preços de fornecedores específicos. O ponto é: **quanto mais você vende, mais o modelo de custo pesa**. Faça a conta com o seu volume antes de escolher.',
      },
      { type: 'h2', text: 'Checklist para escolher' },
      {
        type: 'ol',
        items: [
          'Tem teste grátis longo o suficiente para pegar pelo menos dois fins de semana?',
          'O preço é fixo ou cresce com o número de pedidos?',
          'O dinheiro do PIX e do cartão cai direto na sua conta?',
          'Atende também o salão (QR Code na mesa) se você tiver ou planejar ter?',
          'Tem estoque, financeiro e nota fiscal, ou você vai precisar de outro sistema?',
          'Dá para cancelar sem multa?',
        ],
      },
      { type: 'h2', text: 'Como migrar em uma semana' },
      {
        type: 'table',
        head: ['Dia', 'O que fazer'],
        rows: [
          ['1', 'Criar a conta, cadastrar logo, horários e dados da loja'],
          ['2', 'Montar o cardápio: categorias, produtos, fotos, tamanhos e adicionais'],
          ['3', 'Configurar entrega (taxa por bairro), retirada e formas de pagamento'],
          ['4', 'Conectar o WhatsApp e fazer pedidos de teste com a equipe'],
          ['5', 'Treinar a equipe no painel: aceitar, mudar status, imprimir'],
          ['6 e 7', 'Divulgar o link e acompanhar o primeiro fim de semana de perto'],
        ],
      },
      { type: 'h2', text: 'Onde divulgar o link da sua loja' },
      {
        type: 'ul',
        items: [
          '**Bio do Instagram** e destaques com o passo a passo para pedir.',
          '**Mensagem de boas-vindas do WhatsApp** e status semanal com o cardápio do dia.',
          '**Perfil da empresa no Google**, no campo de pedidos ou site.',
          '**Embalagens e sacolas**: um adesivo com QR Code leva o cliente do marketplace para o seu canal.',
          '**Balcão e mesas**: cartaz com QR Code para pedidos de retirada e consumo no local.',
        ],
      },
      { type: 'h2', text: 'Erros comuns ao escolher' },
      {
        type: 'ul',
        items: [
          'Olhar só o preço da mensalidade e esquecer **taxas por pedido** ou limites de pedidos por plano.',
          'Contratar um sistema só de cardápio e depois precisar de **outro para estoque, financeiro e nota fiscal**.',
          'Não testar em um **dia de pico** antes de decidir.',
          'Aceitar **fidelidade contratual** longa sem ter testado o suporte.',
        ],
      },
      {
        type: 'p',
        text: 'Para uma visão sobre usar marketplace e canal próprio ao mesmo tempo, leia [Delivery próprio x marketplace](/blog/delivery-proprio-x-marketplace).',
      },
      {
        type: 'cta',
        text: 'Crie sua loja de delivery própria no Vende Aí: pronta em minutos, com 30 dias grátis e mensalidade fixa.',
      },
    ],
    faq: [
      {
        question: 'Qual a diferença entre sistema de delivery próprio e iFood?',
        answer:
          'O iFood é um marketplace que cobra comissão sobre cada pedido e mantém a relação com o cliente. O sistema próprio é o seu canal: o cliente pede no seu link e você paga uma mensalidade.',
      },
      {
        question: 'Quanto tempo leva para colocar um delivery próprio no ar?',
        answer:
          'A loja pode ficar pronta no mesmo dia. O que mais demora é revisar o cardápio com fotos e preços certos; com isso pronto, em uma semana dá para configurar, testar com a equipe e divulgar.',
      },
      {
        question: 'O dinheiro das vendas online cai na minha conta?',
        answer:
          'No Vende Aí, sim: o PIX e o cartão online são recebidos pela conta do banco ou gateway que você conectar, e o sistema não cobra comissão sobre as vendas.',
      },
      {
        question: 'Preciso ter um aplicativo nas lojas de apps?',
        answer:
          'Não é necessário. Uma loja online com link próprio abre no navegador do celular e pode ser instalada como aplicativo (PWA), sem passar pelas lojas de apps.',
      },
    ],
  },
  {
    slug: 'como-escolher-sistema-para-restaurante',
    title: 'Como escolher um sistema para restaurante: 12 perguntas antes de contratar',
    description:
      'Checklist com 12 perguntas para escolher um sistema para restaurante, delivery ou lanchonete sem arrependimento: preço, taxas, recursos, suporte e contrato.',
    excerpt:
      'Antes de trocar ou contratar um sistema, faça estas 12 perguntas. Elas evitam taxa escondida, migração dolorosa e recurso que falta no dia a dia.',
    category: 'Gestão',
    keywords: [
      'como escolher sistema para restaurante',
      'melhor sistema para restaurante',
      'sistema para lanchonete',
      'sistema de gestão para restaurante',
      'software para restaurante',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'Trocar de sistema no meio da operação dá trabalho, então vale escolher bem na primeira vez. Estas perguntas servem para comparar qualquer fornecedor, inclusive o Vende Aí.',
      },
      { type: 'h2', text: 'Custo' },
      {
        type: 'ol',
        items: [
          '**O preço é fixo ou muda com o número de pedidos?** Planos por faixa ficam caros justamente quando você cresce.',
          '**Existe comissão sobre as vendas?** Some tudo: mensalidade, comissão e taxa por pedido.',
          '**Tem taxa de implantação ou de cancelamento?**',
          '**Qual o prazo do teste grátis?** Sete dias raramente pegam dois fins de semana.',
        ],
      },
      { type: 'h2', text: 'Operação' },
      {
        type: 'ol',
        items: [
          '**Delivery, balcão e salão ficam no mesmo painel?**',
          '**O pedido do WhatsApp entra sozinho ou alguém redigita?**',
          '**Funciona no computador e no celular que você já tem?**',
          '**Imprime o pedido na cozinha e tem painel de fila para TV?**',
        ],
      },
      { type: 'h2', text: 'Dinheiro e gestão' },
      {
        type: 'ol',
        items: [
          '**O PIX e o cartão caem direto na sua conta?** E o PIX é confirmado automaticamente?',
          '**Tem estoque com ficha técnica, financeiro e nota fiscal?** Ou você vai precisar de outro sistema?',
        ],
      },
      { type: 'h2', text: 'Relação com o fornecedor' },
      {
        type: 'ol',
        items: [
          '**Os dados dos clientes são seus e dá para exportar?**',
          '**Como é o suporte e o que acontece se você quiser cancelar?**',
        ],
      },
      {
        type: 'tip',
        title: 'Faça o teste no pico',
        text: 'Use o período de teste numa sexta ou sábado à noite. É no pico que um sistema mostra se ajuda ou atrapalha.',
      },
      {
        type: 'p',
        text: 'Se você tem pizzaria, hamburgueria, restaurante, padaria ou barbearia, veja o que o Vende Aí faz para o [seu tipo de negócio](/#segmentos).',
      },
      {
        type: 'cta',
        text: 'Compare na prática: use o Vende Aí completo por 30 dias, sem cartão e sem fidelidade.',
      },
    ],
    faq: [
      {
        question: 'Qual o melhor sistema para restaurante?',
        answer:
          'O melhor é o que atende a sua operação pelo menor custo total. Compare preço fixo x por pedido, comissões, recursos de delivery e salão, pagamento direto na sua conta, estoque, nota fiscal e condições de cancelamento.',
      },
      {
        question: 'Quanto tempo leva para implantar um sistema de restaurante?',
        answer:
          'Em sistemas self-service, a loja fica pronta no cadastro e o cardápio pode ser montado em algumas horas ou importado. No Vende Aí dá para começar a vender no mesmo dia.',
      },
    ],
  },
];
